/**
 * Фільтр списку людською мовою (FLT-10, FLT-11): текст чипа «Етап: А, Б» і поділ дерева на чипи.
 * Модуль — у першому екрані (чипи видно одразу), без UI й без операторів за типом; зведення під
 * конструктором — `tools/summary-text.ts` (лінивий чанк). Дерево — з `@clientline/contracts/filter-tree`:
 * вхід `filter-eval` тягнув би в перший екран оцінювач і довідник валют (рев'ю Frontend #842).
 *
 * Чипи (FLT-10): корінь — `and` з умов без вкладених груп або одна умова — чип на кожну умову; корінь
 * `or` чи є група — один чип «Розширений фільтр · N умов» (FLT-11).
 *
 * Значення: ключі списків — підписи з `/meta`, токени — словник, дати й гроші — формат людини. Люди
 * й записи-зв'язки — кількістю: назв у дереві немає, а окремого запиту чип не робить.
 */
import type {
  FilterCondition,
  FilterItem,
  FilterNode,
  FilterToken,
  MetaEntity,
  MetaField,
} from '@clientline/contracts';
import {
  groupOf,
  isFilterGroup,
  isFilterMoney,
  isFilterToken,
  valuesOf,
} from '@clientline/contracts/filter-tree';
import {
  formatDateOnly,
  formatDateTime,
  formatMoney,
  formatNumber,
  plural,
} from '../../i18n/format.ts';
import { filters as t } from '../../i18n/uk/filters.ts';

/** Умови дерева за порядком (групи розгорнуто). */
export function conditionsOf(tree: FilterNode | null | undefined): FilterCondition[] {
  if (!tree) return [];
  if (isFilterGroup(tree)) return groupOf(tree).items.flatMap((item) => conditionsOf(item));
  return 'field' in tree ? [tree] : [];
}

/**
 * Чипи для дерева: `simple` — умова на чип (корінь `and` без груп чи одна умова), `advanced` — один
 * чип на все дерево (корінь `or` або є група).
 */
export type FilterChips =
  | { kind: 'none' }
  | { kind: 'simple'; conditions: FilterCondition[] }
  | { kind: 'advanced'; count: number };

export function chipsOf(tree: FilterNode | null | undefined): FilterChips {
  if (!tree) return { kind: 'none' };
  if (!isFilterGroup(tree)) return 'field' in tree ? { kind: 'simple', conditions: [tree] } : none;
  const { kind, items } = groupOf(tree);
  const flat = items.every((item) => !isFilterGroup(item) && 'field' in item);
  if (kind === 'and' && flat) return { kind: 'simple', conditions: items as FilterCondition[] };
  return { kind: 'advanced', count: conditionsOf(tree).length };
}
const none: FilterChips = { kind: 'none' };

const fieldOf = (entity: MetaEntity, key: string): MetaField | undefined =>
  entity.fields.find((f) => f.key === key);

export const fieldLabelOf = (entity: MetaEntity, key: string): string =>
  fieldOf(entity, key)?.label.uk ?? t.unknownField(key);

/** Підпис токена: людини, дня, діапазону, моменту. */
export function tokenLabel(token: FilterToken): string {
  const days = token.n !== undefined ? plural(token.n, t.days) : '';
  switch (token.token) {
    case 'days_ago':
      return t.daysAgo(days);
    case 'days_ahead':
      return t.daysAhead(days);
    case 'last_n_days':
      return t.lastNDays(days);
    case 'next_n_days':
      return t.nextNDays(days);
    default:
      return t.token[token.token] ?? token.token;
  }
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Одне значення умови текстом (крім людей і записів: їх — кількістю). */
export function itemLabel(field: MetaField | undefined, item: FilterItem): string {
  if (isFilterToken(item)) return tokenLabel(item);
  if (isFilterMoney(item)) return formatMoney(item.amount, item.currency);
  if (typeof item === 'boolean') return item ? t.yes : t.no;
  // Число на полі-грошах — сума в усіх валютах: без знака валюти.
  if (typeof item === 'number') return formatNumber(item);
  if (field?.type === 'picklist' || field?.type === 'multipicklist') {
    return field.options?.find((o) => o.key === item)?.label.uk ?? item;
  }
  if (field?.type === 'date' && DAY.test(item)) return formatDateOnly(item);
  if (field?.type === 'datetime') {
    if (DAY.test(item)) return formatDateOnly(item);
    if (!Number.isNaN(Date.parse(item))) return formatDateTime(item);
  }
  return item;
}

/** Поле зберігає id людей чи записів: значення показуються кількістю (назв у дереві немає). */
const isIdField = (field: MetaField | undefined) =>
  field?.type === 'user' || field?.type === 'uuid' || field?.type === 'tags';

/** Значення умови текстом: «А, Б», «я, 2 людини», «1 – 5». */
export function valuesLabel(field: MetaField | undefined, condition: FilterCondition): string {
  const items = valuesOf(condition);
  if (condition.op === 'between' && items.length === 2) {
    return t.betweenRange(
      itemLabel(field, items[0] as FilterItem),
      itemLabel(field, items[1] as FilterItem),
    );
  }
  if (isIdField(field)) {
    const tokens = items.filter(isFilterToken).map(tokenLabel);
    const ids = items.filter((i) => !isFilterToken(i)).length;
    const counted = ids > 0 ? [plural(ids, field?.type === 'user' ? t.people : t.records)] : [];
    return [...tokens, ...counted].join(', ');
  }
  const text = items.map((item) => itemLabel(field, item)).join(', ');
  return condition.op === 'starts_with' ? t.quoted(text) : text;
}

/** Значення з коротким оператором: «не А, Б», «від 20 000 ₴», «порожнє». */
export function conditionValueText(entity: MetaEntity, condition: FilterCondition): string {
  if (condition.op === 'is_null') return t.isNull;
  if (condition.op === 'not_null') return t.notNull;
  const field = fieldOf(entity, condition.field);
  const op = t.chipOp[condition.op];
  const values = valuesLabel(field, condition);
  const text = op ? `${op} ${values}` : values;
  return condition.excludeEmpty ? `${text} (${t.excludeEmpty})` : text;
}

/** Текст чипа: «Етап: А, Б». */
export const chipText = (entity: MetaEntity, condition: FilterCondition): string =>
  `${fieldLabelOf(entity, condition.field)}: ${conditionValueText(entity, condition)}`;

/** Кількість умов словами: «5 умов». */
export const conditionCount = (n: number): string => plural(n, t.conditionForms);
